import { apiCall } from '@/lib/api';
import {
  createInfiniteQuery,
  createMutation,
  createQuery,
  useQueryClient,
} from '@tanstack/solid-query';
import type { CreateUserBody, UpdateUserBody, UserStatus } from '@uchat-team-management/shared';
import { createUser, deleteUser, getUser, listUsers, updateUser } from './api';

export { rolesKey, useRoles } from '@/features/roles/hooks';

export interface UsersFilters {
  q?: string;
  status?: UserStatus;
}

export const usersKey = (filters: UsersFilters) => ['users', filters] as const;

export const useUsers = (filters: () => UsersFilters) =>
  createQuery(() => ({
    queryKey: usersKey(filters()),
    queryFn: () => apiCall(listUsers, { query: filters() }),
  }));

// UCH-447: paged admin table — one infinite-query page per page number.
export const useUsersList = (filters: () => UsersFilters) =>
  createInfiniteQuery(() => ({
    queryKey: ['users', 'list', filters()] as const,
    queryFn: ({ pageParam }) =>
      apiCall(listUsers, { query: { ...filters(), cursor: pageParam || undefined } }),
    initialPageParam: '',
    getNextPageParam: (last) => last.meta.nextCursor ?? undefined,
  }));

// Mutations invalidate the whole ['users'] prefix, which covers this detail key too.
export const useUser = (userId: () => string) =>
  createQuery(() => ({
    queryKey: ['users', 'detail', userId()] as const,
    queryFn: () => apiCall(getUser, { params: { userId: userId() } }),
  }));

export const useInviteUser = () => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (body: CreateUserBody) => apiCall(createUser, { body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  }));
};

export const useUpdateUser = (userId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: (body: UpdateUserBody) => apiCall(updateUser, { params: { userId }, body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  }));
};

export const useDeleteUser = (userId: string) => {
  const qc = useQueryClient();
  return createMutation(() => ({
    mutationFn: () => apiCall(deleteUser, { params: { userId } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  }));
};
